/**
 * The application.
 *
 * Boot in order: read the token, learn what this branch is, load the menu
 * (server first, cache second), replay the outbox, open the socket. Each step
 * has a screen that says what it is waiting for — the old app had a
 * `sessionStorage` flag deciding between a startup page and a memory-history
 * route, and a spinner that could mean any of four things.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import type { Menu } from '@mgc/domain'
import { zMenu } from '@mgc/protocol'
import { isUnenrolled, queryClient, setToken, trpc } from '@/api/client'
import { SocketContext } from '@/api/events'
import { ServerSocket } from '@/api/socket'
import { BasketFlight } from '@/components/basket-flight'
import { IdleGuard } from '@/components/idle-guard'
import { Boot, RecoveryBanner } from '@/screens/boot'
import { Checkout } from '@/screens/checkout'
import { Menu as MenuScreen } from '@/screens/menu'
import { OrderType } from '@/screens/order-type'
import { Pair } from '@/screens/pair'
import { Payment } from '@/screens/payment'
import { ProductModal } from '@/screens/product-modal'
import { Attendant } from '@/screens/attendant'
import { Settings } from '@/screens/settings'
import { Shift } from '@/screens/shift'
import { Success } from '@/screens/success'
import { Welcome } from '@/screens/welcome'
import { replayOutbox } from '@/services/pay'
import { record, ship, watchForCrashes } from '@/services/diagnostics'
import { runReceiptProof } from '@/services/receipt-proof'
import { runTestPrint, watchShellTestPrints } from '@/services/test-print'
import { startHeartbeat } from '@/services/presence'
import { forgetMenu, loadMenu, outstanding, pruneAcknowledged, saveMenu } from '@/outbox/outbox'
import { registerServiceWorker, reloadWhenIdle, watchShellUpdates } from '@/shell/update'
import { looksLikeBarcode, watchWedge } from '@/shell/wedge'
import { offerScan } from '@/services/scan-claim'
import { useKiosk } from '@/store/kiosk'
import { readToken, useSession } from '@/store/session'
import { loadCachedTemplate, refreshTemplate } from '@/services/receipt-template'

type Overlay = 'none' | 'settings' | 'shift' | 'attendant'

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Shell />
    </QueryClientProvider>
  )
}

function Shell() {
  const session = useSession()
  const [boot, setBoot] = useState<'connecting' | 'menu' | 'unenrolled' | 'no-cache' | 'ready'>('connecting')
  const [overlay, setOverlay] = useState<Overlay>('none')
  const [replaying, setReplaying] = useState(false)
  const socketRef = useRef<ServerSocket | null>(null)
  /** The catalogue we hold, so a reconnect can ask "still this?" rather than refetch. */
  const knownRevision = useRef<string | null>(null)
  /**
   * Which shop this is, for the two places that re-read the receipt template.
   *
   * A ref rather than `useSession`, because both readers are callbacks that
   * outlive a render: the socket handler and the freshness poll.
   */
  const branchId = useRef<string | null>(null)

  const refreshOutbox = useCallback(async () => {
    useSession.getState().setOutbox(await outstanding())
  }, [])

  /** Server first, cache second. A cached menu is a working kiosk; no menu is not. */
  const loadCatalogue = useCallback(async (knownRevisionArg?: string) => {
    /*
     * Parsed, not cast.
     *
     * This was `wire as unknown as Menu`, which is what makes a shape change
     * silent: the document renders wrong rather than failing, and the failure
     * shows up as a customer looking at an empty menu. The parse is deliberately
     * **outside** the network `catch` below — a rejected document is not an
     * offline kiosk, and treating it as one would hide the deploy that caused it.
     */
    let wire: unknown
    try {
      const result = await trpc.catalogue.menu.query(
        knownRevisionArg === undefined ? undefined : { knownRevision: knownRevisionArg }
      )
      knownRevision.current = result.revision
      if (result.unchanged) return true
      wire = result.menu
      useKiosk.getState().setOnline(true)
    } catch {
      useKiosk.getState().setOnline(false)
      // Keyed by branch: a kiosk re-enrolled to another shop must not boot
      // offline onto the previous shop's prices.
      const branchId = useSession.getState().branch?.branchId
      const cached = branchId === undefined ? undefined : await loadMenu(branchId)
      if (cached === undefined) return false
      /*
       * The cache is parsed too, and a document that fails is dropped rather
       * than served. A kiosk that went offline holding a pre-migration menu has
       * no other way to notice; the alternative is selling from a document
       * nothing agrees about.
       */
      const held = zMenu.safeParse(cached.document)
      if (!held.success) {
        await forgetMenu()
        return false
      }
      knownRevision.current = cached.revision
      useKiosk.getState().setMenu(held.data as unknown as Menu, cached.servedAt)
      return true
    }

    const parsed = zMenu.safeParse(wire)
    if (!parsed.success) {
      // Loud, and not as an outage: the server sent something this bundle does
      // not understand, which is a deploy that has gone wrong rather than a
      // shop that has lost its uplink.
      console.error('[menu] the server sent a document this kiosk cannot read', parsed.error.issues.slice(0, 3))
      return false
    }
    const menu = parsed.data as unknown as Menu
    useKiosk.getState().setMenu(menu, menu.servedAt)
    await saveMenu(parsed.data.branchId, knownRevision.current ?? '', menu.servedAt, menu)
    return true
  }, [])

  /**
   * A cheap check that the catalogue we hold is still current.
   *
   * `catalogue.revision` is a hash and a boolean where `catalogue.menu` is the
   * whole document, so this is what runs on every reconnect. A fleet coming
   * back online together must not each re-download a menu they already have.
   */
  const checkFreshness = useCallback(async () => {
    try {
      const [revision, status] = await Promise.all([trpc.catalogue.revision.query(), trpc.branch.status.query()])
      useKiosk.getState().setOnline(true)
      useKiosk.getState().setBranchClosed(status.isClosed)
      if (revision.revision !== knownRevision.current) await loadCatalogue(knownRevision.current ?? undefined)
      // A chain-wide receipt change is announced to nobody — a push per branch
      // is two thousand messages for a footer — so this is where it lands. It
      // passes the revision in hand, so the usual answer is a boolean.
      if (branchId.current !== null) void refreshTemplate(branchId.current)
      return status
    } catch {
      useKiosk.getState().setOnline(false)
      return null
    }
  }, [])

  const start = useCallback(async () => {
    setBoot('connecting')
    const { token, source } = await readToken()
    if (token === null) {
      setBoot('unenrolled')
      return
    }

    setToken(token)
    session.setToken(token, source)

    let refused = false
    try {
      // One request, not two. Neither call needs the other's answer, and the
      // client batches whatever is issued in the same tick — so awaiting the
      // first before issuing the second spent a whole round trip of a shop's
      // uplink, on the screen a customer is standing in front of.
      const [who, config] = await Promise.all([trpc.devices.whoami.query(), trpc.branch.config.query()])
      session.setDeviceName(who.deviceName)
      session.setBranch(config)
      useKiosk.getState().setBranchClosed(config.isClosed)
      useKiosk.getState().setOnline(true)
      branchId.current = config.branchId

      /*
       * The receipt's arrangement, cache first and network second.
       *
       * The cached document is awaited and the fetch is not: what a slip is
       * printed from has to be in hand before a sale can complete, and a
       * network read of it must never be. Deliberately **not** inside the
       * `Promise.all` above — that pair's `catch` means «offline», and a
       * template that failed for its own reasons must not put a kiosk that can
       * still sell onto the offline path. Whatever is in hand still prints.
       */
      await loadCachedTemplate(config.branchId)
      void refreshTemplate(config.branchId)
    } catch (error) {
      // Offline, or revoked. Both leave the cached menu as the only path, and
      // the boot screen below says which one we could not get past.
      refused = isUnenrolled(error)
      useKiosk.getState().setOnline(false)
    }

    setBoot('menu')
    const ok = await loadCatalogue()
    if (!ok) {
      // Cold and offline. `docs/offline.md` says this has no mitigation in this
      // architecture, so the screen states it rather than spinning.
      //
      // Cold and *refused* is not that. The server answered and does not know
      // this token, so the only way forward is enrolling again — and the pairing
      // screen overwrites the token, so nothing has to be cleared first.
      setBoot(refused ? 'unenrolled' : 'no-cache')
      return
    }

    await refreshOutbox()
    setBoot('ready')

    // The socket, and the replay it triggers on every reconnect.
    socketRef.current?.close()
    const socket = new ServerSocket(token)
    socketRef.current = socket
    socket.on((event) => {
      if (event.type === 'catalogue-changed') {
        // Pass what we hold, so an event for a revision we already have costs
        // a boolean instead of the whole document. The revision on the event
        // was being discarded and every push refetched the lot.
        void loadCatalogue(knownRevision.current ?? undefined)
      }
      if (event.type === 'branch-status') useKiosk.getState().setBranchClosed(event.isClosed)
      // This shop's receipt was rearranged in the console. Re-read rather than
      // be told: the event carries no document, so a kiosk that already has
      // this revision costs a boolean.
      if (event.type === 'receipt-template-changed' && branchId.current !== null) {
        void refreshTemplate(branchId.current)
      }
      // Somebody edited this shop in the console — its name, its address, the
      // WiFi that `packages/receipt` prints on every slip. Re-read rather than
      // be told, so one event covers every field.
      if (event.type === 'branch-config-changed') {
        void trpc.branch.config
          .query()
          .then((config) => {
            useSession.getState().setBranch(config)
            useKiosk.getState().setBranchClosed(config.isClosed)
            record('info', 'branch configuration reloaded')
          })
          .catch(() => record('warn', 'branch configuration changed but could not be re-read'))
      }
      // The console asked for a bundle, or for a reload. The reload waits for
      // an idle kiosk, the same verdict a service-worker swap does: an operator
      // at a desk cannot see whether a customer is halfway through a basket.
      if (event.type === 'report-requested') void ship('requested')
      /*
       * Print, say what happened, and send the bundle carrying it.
       *
       * The shipping is the point: a remote test print whose result nobody can
       * read is half a feature, and the console has no other way to learn
       * whether paper came out. Failures are recorded too — a printer that
       * refused is exactly what the operator asked the question to find out.
       */
      if (event.type === 'test-print-requested') {
        void runTestPrint()
          .then((outcome) => record('info', `test print requested from the console: ${outcome.summary}`))
          .catch((error: unknown) => record('error', `test print requested from the console failed: ${String(error)}`))
          .finally(() => void ship('requested'))
      }
      /*
       * A receipt somebody is still arranging, printed once to see what the
       * paper does with it.
       *
       * No bundle afterwards, and that is the difference from the line above.
       * A test print's answer has nowhere else to go, so it is shipped; a
       * proof writes its outcome to its own row and the console is already
       * watching that row — a diagnostic bundle as well would be a second,
       * slower copy of an answer the operator has had for ten seconds.
       */
      if (event.type === 'receipt-proof-requested') void runReceiptProof(event.proofId)
      // A payment that failed while a QR code is on screen. The payment screen
      // listens too; this is the record and the reset for the case the modal
      // has already been dismissed.
      if (event.type === 'payment-failed') record('warn', `payment ${event.paymentId} failed: ${event.reason}`)
      // A shift opened or closed on a sibling kiosk. The shift screen reads
      // server state, so all this has to do is stop showing a stale answer.
      if (event.type === 'shift-changed') {
        void queryClient.invalidateQueries({ queryKey: ['shift'] })
        record('info', event.shiftId === null ? 'shift closed' : `shift ${event.shiftId} opened`)
      }
      if (event.type === 'reload-requested') {
        record('info', `reload requested: ${event.reason}`)
        // Idle, not merely money-free: this operator is at a desk and cannot
        // see whether somebody is standing at the machine. The shell's menu
        // item is the case that can, and `canReloadOnRequest` is its rule.
        reloadWhenIdle()
      }
    })
    socket.connect()

    record('info', `booted · build ${__BUILD_ID__}`)
    void ship('startup')
    void replay()
  }, [loadCatalogue, refreshOutbox])

  const replay = useCallback(async () => {
    setReplaying(true)
    try {
      const result = await replayOutbox()
      if (result.sent > 0) record('info', `replayed ${result.sent} outbox entries`)
      if (result.rejected > 0) record('error', `${result.rejected} outbox entries were refused by the server`)
      await refreshOutbox()
      // Acknowledged entries older than a week are dropped; nothing
      // unacknowledged is ever touched.
      await pruneAcknowledged(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
    } finally {
      setReplaying(false)
    }
  }, [refreshOutbox])

  useEffect(() => {
    void start()
    return () => socketRef.current?.close()
  }, [start])

  /*
   * The document's language follows the customer's.
   *
   * `index.html` ships `lang="mn"` and nothing ever wrote it again, so a
   * customer who switched to English got English text under a Mongolian
   * document — which is not cosmetic: a screen reader picks its voice and its
   * pronunciation rules from this attribute, and every browser feature keyed on
   * document language (translation offers, hyphenation, font fallback) reads it
   * too. It belongs here rather than in `LocaleSwitch` because the locale also
   * changes on `reset()`, which no switch is holding.
   */
  const locale = useKiosk((s) => s.locale)
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  // Connectivity, replay on reconnect, and the socket's own liveness.
  useEffect(() => {
    const online = () => {
      void replay()
      void checkFreshness()
    }
    const offline = () => useKiosk.getState().setOnline(false)
    window.addEventListener('online', online)
    window.addEventListener('offline', offline)

    const poll = setInterval(() => {
      useSession.getState().setSocketConnected(socketRef.current?.connected ?? false)
    }, 3_000)

    return () => {
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
      clearInterval(poll)
    }
  }, [replay, checkFreshness])

  // The bundle and the shell both hold their updates until the store can afford
  // one. See src/shell/update.ts.
  useEffect(() => {
    const stopSw = registerServiceWorker()
    const stopShell = watchShellUpdates()
    const stopCrashes = watchForCrashes()
    // The shell's setup window has the same test-print button and cannot draw a
    // receipt; this is what answers it. See services/test-print.ts.
    const stopTestSlips = watchShellTestPrints()
    // The beat that keeps `device.lastSeenAt` true. Without it the fleet
    // console marks every working kiosk as silent.
    const stopHeartbeat = startHeartbeat()
    // A bundle every hour, so a kiosk that has been quietly wrong for a day is
    // visible before somebody rings about it.
    const periodic = setInterval(() => void ship('periodic'), 3600_000)
    return () => {
      stopSw()
      stopShell()
      stopCrashes()
      stopTestSlips()
      stopHeartbeat()
      clearInterval(periodic)
    }
  }, [])

  /**
   * The way in to the attendant screens.
   *
   * A long press in the top-left corner: no visible affordance for a customer,
   * no keyboard shortcut for a scanner to trigger by accident, and reachable
   * with one hand on a panel nobody wants to look like it has settings.
   *
   * **It is a press and not merely a contact**, which it was: the timer started
   * on `pointerdown` and only `pointerup` stopped it, so anything that rested
   * in that corner for two and a half seconds opened the attendant screens over
   * the customer's order. A palm on the bezel while somebody reads the menu, a
   * second finger set down beside a scrolling one, a sleeve — none of those are
   * a press, and the corner is the top-left of a header that scrolls nothing,
   * so `pointercancel` (which a scroll would have raised) never came either.
   * A press that travels is a press abandoned, and `isPrimary` keeps a second
   * finger from starting one at all.
   */
  useEffect(() => {
    /** How far a finger may drift and still be held still. Half a fingertip. */
    const SLOP_PX = 16
    let timer: ReturnType<typeof setTimeout> | null = null
    let origin: { x: number; y: number } | null = null

    const stop = () => {
      if (timer !== null) clearTimeout(timer)
      timer = null
      origin = null
    }
    const down = (event: PointerEvent) => {
      if (!event.isPrimary || event.clientX > 120 || event.clientY > 120) return
      origin = { x: event.clientX, y: event.clientY }
      timer = setTimeout(() => {
        stop()
        setOverlay('settings')
      }, 2_500)
    }
    const move = (event: PointerEvent) => {
      if (origin === null) return
      if (Math.abs(event.clientX - origin.x) > SLOP_PX || Math.abs(event.clientY - origin.y) > SLOP_PX) stop()
    }

    window.addEventListener('pointerdown', down)
    window.addEventListener('pointermove', move, { passive: true })
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)
    // A window that loses the pointer — the shell's own menu, a screensaver —
    // must not come back to a gesture that is still counting.
    window.addEventListener('blur', stop)
    return () => {
      stop()
      window.removeEventListener('pointerdown', down)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', stop)
      window.removeEventListener('pointercancel', stop)
      window.removeEventListener('blur', stop)
    }
  }, [])

  /**
   * The scanner, read as a keyboard.
   *
   * Two things arrive on this wire and they are told apart by shape. A numeric
   * 8–14 digit code is a **product barcode**, and on the menu it adds that
   * product to the basket — the one place scanning is faster than tapping.
   * Anything else is a **staff badge**, which opens the attendant screens.
   *
   * Its failure mode is a manual field rather than a stop: a scan that matches
   * nothing is a log line and a shrug, because the customer can always tap.
   */
  useEffect(
    () =>
      watchWedge((code) => {
        record('info', `scan: ${code.slice(0, 4)}…`)

        // A screen waiting for a code — the coupon — takes it before the shape
        // decides what it is. See `services/scan-claim.ts`.
        if (offerScan(code)) return

        if (!looksLikeBarcode(code)) {
          // A badge. Only from the welcome screen, so a scanner pointed at a
          // basket cannot take a customer out of their order.
          if (useKiosk.getState().step === 'welcome') setOverlay('shift')
          return
        }

        const state = useKiosk.getState()
        const product = state.menu?.products.find((p) => p.id === code || p.sku === code)
        if (product === undefined) {
          record('warn', `scanned ${code}, which is not on this menu`)
          return
        }
        // Straight into the modal rather than the basket: a drink has options
        // somebody has to choose, and adding it silently at its base price is
        // an order the barista cannot make.
        if (state.step === 'menu' || state.step === 'checkout') state.openProductModal(product)
      }),
    []
  )

  if (boot === 'unenrolled') return <Pair onPaired={() => void start()} />
  if (boot !== 'ready') return <Boot state={boot} onRetry={() => void start()} />

  return (
    <SocketContext.Provider value={socketRef.current}>
      <div className="flex h-full flex-col">
        {/*
          A stuck claim needs a person, and the attendant screen is where that
          person acts — so the banner hands off to it rather than being a
          message with no way out.
        */}
        <RecoveryBanner onRetry={() => void replay()} retrying={replaying} onResolve={() => setOverlay('attendant')} />

        <div className="min-h-0 flex-1">
          {overlay === 'settings' ? (
            <Settings onClose={() => setOverlay('none')} />
          ) : overlay === 'shift' ? (
            <Shift onClose={() => setOverlay('none')} />
          ) : overlay === 'attendant' ? (
            <Attendant onClose={() => setOverlay('none')} />
          ) : (
            <Screen />
          )}
        </div>

        {/*
          The attendant screens are siblings, not a hierarchy: somebody who has
          got this far is behind the counter and should not have to go back out
          to the menu to move between them.
        */}
        {overlay !== 'none' && (
          <nav className="flex gap-3 border-t border-[var(--color-line)] p-4">
            {(['attendant', 'shift', 'settings'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                aria-current={overlay === tab}
                className={`btn-ghost ${overlay === tab ? 'border-[var(--color-brand-strong)]' : ''}`}
                onClick={() => setOverlay(tab)}
              >
                {tab}
              </button>
            ))}
          </nav>
        )}
      </div>

      <ProductModal />
      <BasketFlight />
      {/*
        The session boundary. Mounted once, above every step, because the thing
        it protects against — a basket left on the glass for the next customer —
        is not the property of any one screen.
      */}
      <IdleGuard />
    </SocketContext.Provider>
  )
}

function Screen() {
  const step = useKiosk((s) => s.step)
  switch (step) {
    case 'welcome':
      return <Welcome />
    case 'order-type':
      return <OrderType />
    case 'menu':
      return <MenuScreen />
    case 'checkout':
      return <Checkout />
    case 'payment':
      return <Payment />
    case 'success':
      return <Success />
  }
}
